// src/utils/mentions.ts — SCE Issues
// Mentionner quelqu'un avec « @ » (02/11, demande de Guillaume) dans un
// commentaire ou une description : la personne reçoit une notification à elle,
// sauf si elle en reçoit déjà une pour ce geste. Pur, testable hors ligne.
//
// ─── Le texte ───
//
// Une mention s'écrit « @Prénom Nom » dans le texte, tel quel : lisible dans
// SharePoint, dans les flows (le flow 4 cite le commentaire) et dans l'export.
// Rien n'est stocké à côté : la personne (son adresse) est connue de l'onglet
// qui l'a choisie dans le menu, le temps de l'envoi. Un « @Thibaut » tapé à la
// main, sans passer par le menu, ne prévient personne.
//
// ─── Qui est prévenu ───
//
// Chaque personne mentionnée, SAUF :
// - l'auteur lui-même ;
// - qui reçoit déjà un message pour ce geste :
//     · commentaire, clôture, annulation (un événement de l'activité) : ceux
//       qui suivent le problème — le flow 4 leur envoie déjà le texte ;
//     · déclaration : coordinateurs, suppléants et responsables du sujet du
//       problème, et ceux qui suivent son projet ou son équipement — le flow 1
//       les alerte déjà ;
//     · description corrigée, commentaire corrigé : pas d'événement, donc
//       personne n'est prévenu d'autre part (abonnés compris) ;
// - qui a réglé « l'appli seulement » (`Appli`, ou l'ancien « Aucun » pour
//   lequel le flow 6 enverrait le mail quand même) : pas de message, mais sa
//   ligne dans la cloche (api/mentions, 03/11).
// Une correction ne prévient que des mentions AJOUTÉES.
//
// ─── L'envoi ───
//
// Comme les notifications FNC-T : l'appli compose tout (objet, mail HTML,
// carte Teams) et dépose une ligne dans SCE_Envois, le flow 6 l'envoie selon
// le canal de chacun. Title = « Mention P2026-0042 » : la page « Pour le BE »
// ne la prend pas pour un envoi au BE (utils/pageBE).

import type { Abonne, Issue } from '@/types/issue.types'
import { METIERS_LABELS } from '@/config/metiers.config'
import { appliSeulement } from './canauxNotification'
import { suit } from './elementsSuivis'
import {
  carteAdaptative,
  echapper,
  enHtml,
  enteteCarte,
  enveloppeMail,
  lignesFaits,
  sectionMail,
  texteCarte,
  TEXTE_MAIL,
  tronquer,
  type BlocCarte,
} from './gabaritMessage'

export interface PersonneMentionnee {
  /** « Prénom Nom », tel qu'écrit après « @ ». */
  nom: string
  upn: string
}

// ─── Saisie ───

/** Ce qui peut précéder « @ » : début, blanc, ouvrante. Pas une lettre (adresse mail). */
const AVANT_AROBASE = /[\s([{«"'’]/
/** Au-delà, ce n'est plus un nom qu'on tape. */
const REQUETE_MAX = 40

export interface RequeteMention {
  /** Position du « @ ». */
  debut: number
  /** Ce qui est tapé après lui, jusqu'au curseur. */
  texte: string
}

/**
 * La mention en cours de saisie sous le curseur, ou null : un « @ » en début
 * de mot, suivi d'au plus trois mots sans retour à la ligne.
 */
export function requeteMention(texte: string, curseur: number): RequeteMention | null {
  const avant = texte.slice(0, Math.max(0, curseur))
  const debut = avant.lastIndexOf('@')
  if (debut < 0) return null
  if (debut > 0 && !AVANT_AROBASE.test(avant[debut - 1] ?? '')) return null
  const q = avant.slice(debut + 1)
  if (q.length > REQUETE_MAX || /[\r\n@]/.test(q) || /^\s/.test(q) || /\s\s/.test(q)) return null
  if (q.trim().split(/\s+/).length > 3) return null
  return { debut, texte: q }
}

/** Remplace « @thib » (du « @ » au curseur) par « @Thibaut Hacquin » suivi d'un espace. */
export function insererMention(texte: string, debut: number, curseur: number, nom: string): { texte: string; curseur: number } {
  const apres = texte.slice(curseur)
  const insere = `@${nom}${/^\s/.test(apres) ? '' : ' '}`
  return { texte: texte.slice(0, debut) + insere + apres, curseur: debut + insere.length }
}

const echapperRegex = (t: string): string => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** « @Thibaut Hacquin » est-il dans le texte (pas « @Thibaut Hacquinet ») ? */
function contientMention(texte: string, nom: string): boolean {
  if (!nom.trim()) return false
  return new RegExp(`@${echapperRegex(nom.trim())}(?![\\p{L}\\p{N}])`, 'iu').test(texte)
}

/** Les personnes connues dont la mention est dans le texte, sans doublon. */
export function mentionsDans(texte: string, connues: readonly PersonneMentionnee[]): PersonneMentionnee[] {
  const vus = new Set<string>()
  const res: PersonneMentionnee[] = []
  for (const p of connues) {
    const cle = p.upn.trim().toLowerCase()
    if (!cle || vus.has(cle) || !contientMention(texte, p.nom)) continue
    vus.add(cle)
    res.push(p)
  }
  return res
}

/** Une correction : les mentions du nouveau texte qui n'étaient pas dans l'ancien. */
export function nouvellesMentions(avant: string, apres: string, connues: readonly PersonneMentionnee[]): PersonneMentionnee[] {
  return mentionsDans(apres, connues).filter((p) => !contientMention(avant, p.nom))
}

// ─── Affichage ───

/**
 * « @Prénom Nom » dans un texte affiché : deux mots à majuscule après un « @ »
 * en début de mot (« Jean-Philippe Geny », « Charles LOUIS »). Un nom en trois
 * mots n'est marqué que sur les deux premiers : rien de plus grave.
 */
const MOTIF_AFFICHAGE = /(^|[\s([{«"'’])(@\p{Lu}[\p{L}'’-]*\s\p{Lu}[\p{L}'’-]*)/gu

export type MorceauTexte = { mention: boolean; texte: string }

/** Le texte en morceaux : les mentions à part, pour les marquer. */
export function morceauxMentions(texte: string): MorceauTexte[] {
  const res: MorceauTexte[] = []
  let dernier = 0
  for (const m of texte.matchAll(MOTIF_AFFICHAGE)) {
    const debut = (m.index ?? 0) + (m[1]?.length ?? 0)
    const mention = m[2] ?? ''
    if (debut > dernier) res.push({ mention: false, texte: texte.slice(dernier, debut) })
    res.push({ mention: true, texte: mention })
    dernier = debut + mention.length
  }
  if (dernier < texte.length) res.push({ mention: false, texte: texte.slice(dernier) })
  return res
}

// ─── Qui prévenir ───

/** Où la mention a été écrite. */
export type GesteMention = 'declaration' | 'commentaire' | 'cloture' | 'annulation' | 'description' | 'correction'

/** Ce qu'il faut savoir d'une personne mentionnée (sa ligne de SCE_UserPrefs). */
export interface ReglagesMentionne {
  /** CanauxNotification brut : null = jamais réglé (les deux). */
  canaux: string | null
  projets: readonly string[]
  equipements: readonly string[]
}

export const REGLAGES_INCONNUS: ReglagesMentionne = { canaux: null, projets: [], equipements: [] }

export type RaisonEcart = 'auteur' | 'abonne' | 'alerte' | 'aucun'

export interface ContexteDestinataires {
  geste: GesteMention
  /** Adresses de l'auteur (compte secondaire, tablette partagée compris). */
  auteur: readonly string[]
  issue: Pick<Issue, 'numeroSerie' | 'pn'> & { abonnes: readonly Abonne[] }
  /** Coordinateur, suppléant ou responsable du sujet de CE problème (piloteLeProbleme). */
  pilote: (upn: string) => boolean
  /** Identifiant SharePoint de chaque personne mentionnée, quand un abonné n'a pas d'adresse lisible. */
  idsSharePoint?: ReadonlyMap<string, number>
  /** Par adresse EN MINUSCULES ; absente = jamais réglé. */
  reglages: ReadonlyMap<string, ReglagesMentionne>
}

export interface Destinataires {
  prevenus: PersonneMentionnee[]
  ecartes: { personne: PersonneMentionnee; raison: RaisonEcart }[]
}

/** Le geste a-t-il un événement dans l'activité (que le flow 4 envoie aux abonnés) ? */
const AVEC_EVENEMENT: Readonly<Record<GesteMention, boolean>> = {
  declaration: false,
  commentaire: true,
  cloture: true,
  annulation: true,
  description: false,
  correction: false,
}

function raisonEcart(p: PersonneMentionnee, ctx: ContexteDestinataires): RaisonEcart | null {
  const cle = p.upn.trim().toLowerCase()
  if (ctx.auteur.some((a) => a.trim().toLowerCase() === cle)) return 'auteur'
  const reglages = ctx.reglages.get(cle) ?? REGLAGES_INCONNUS
  if (AVEC_EVENEMENT[ctx.geste]) {
    const id = ctx.idsSharePoint?.get(cle)
    if (ctx.issue.abonnes.some((a) => a.email?.trim().toLowerCase() === cle || (id !== undefined && a.lookupId === id))) return 'abonne'
  }
  if (ctx.geste === 'declaration') {
    if (ctx.pilote(cle)) return 'alerte'
    if (ctx.issue.pn && suit(reglages.projets, ctx.issue.pn)) return 'alerte'
    if (ctx.issue.numeroSerie && suit(reglages.equipements, ctx.issue.numeroSerie)) return 'alerte'
  }
  if (appliSeulement(reglages.canaux)) return 'aucun'
  return null
}

/** Qui reçoit la notification de mention, et pourquoi les autres non. */
export function destinatairesMention(mentionnes: readonly PersonneMentionnee[], ctx: ContexteDestinataires): Destinataires {
  const res: Destinataires = { prevenus: [], ecartes: [] }
  const vus = new Set<string>()
  for (const p of mentionnes) {
    const cle = p.upn.trim().toLowerCase()
    if (!cle.includes('@') || vus.has(cle)) continue
    vus.add(cle)
    const raison = raisonEcart(p, ctx)
    if (raison) res.ecartes.push({ personne: p, raison })
    else res.prevenus.push(p)
  }
  return res
}

/** « · mention envoyée à Hugo Peultier », pour le message de confirmation ; vide sans personne. */
export function resumeMentions(prevenus: readonly PersonneMentionnee[], echec = false, essai = false): string {
  if (echec) return ' · mention non envoyée'
  if (essai) return ' · mention d’essai envoyée à vous seul'
  if (prevenus.length === 0) return ''
  const noms = prevenus.map((p) => p.nom)
  const liste = noms.length === 1 ? noms[0] : `${noms.slice(0, -1).join(', ')} et ${noms[noms.length - 1]}`
  return ` · mention envoyée à ${liste}`
}

// ─── Le message ───

export interface ContenuMention {
  issue: Pick<Issue, 'id' | 'titre' | 'metier' | 'statut' | 'numeroSerie' | 'pn'>
  modele?: string
  /** Qui mentionne (nom du compte réel). */
  auteur: string
  geste: GesteMention
  /** Le texte où figure la mention. */
  texte: string
  /** Les personnes prévenues : leur mention est mise en gras. */
  prevenus: readonly PersonneMentionnee[]
  /** Adresse de la fiche dans l'appli. */
  lien: string
  /** Pendant l'essai (config/mentions) : qui l'aurait reçu, qui aurait été écarté. */
  essai?: Destinataires
}

const RAISONS_ECART: Readonly<Record<RaisonEcart, string>> = {
  auteur: 'c’est l’auteur',
  abonne: 'suit déjà le problème',
  alerte: 'déjà alerté du nouveau problème',
  aucun: 'notifications dans l’appli seulement',
}

/** Le bandeau de l'essai : en production, qui reçoit, qui est écarté et pourquoi. */
export function texteEssaiMention(d: Destinataires): string {
  const recus = d.prevenus.length > 0 ? d.prevenus.map((p) => p.nom).join(', ') : 'personne'
  const ecartes = d.ecartes.map((e) => `${e.personne.nom} (${RAISONS_ECART[e.raison]})`).join(', ')
  return `Essai des mentions : en production, ce message irait à ${recus}.${ecartes ? ` Écarté${d.ecartes.length > 1 ? 's' : ''} : ${ecartes}.` : ''}`
}

export interface MessageMention {
  objet: string
  corpsHtml: string
  carteTeams: Record<string, unknown>
}

/** Longueur du texte cité dans le message. */
export const LONGUEUR_CITATION = 1500

const OU: Readonly<Record<GesteMention, { phrase: string; section: string }>> = {
  declaration: { phrase: 'vous a mentionné en déclarant ce problème.', section: 'Description' },
  commentaire: { phrase: 'vous a mentionné dans un commentaire.', section: 'Commentaire' },
  cloture: { phrase: 'vous a mentionné en clôturant ce problème.', section: 'Commentaire de clôture' },
  annulation: { phrase: 'vous a mentionné en annulant ce problème.', section: 'Commentaire d’annulation' },
  description: { phrase: 'vous a mentionné dans la description de ce problème.', section: 'Description' },
  correction: { phrase: 'vous a mentionné en corrigeant son commentaire.', section: 'Commentaire' },
}

function faitsMention(c: ContenuMention): Array<[string, string]> {
  const faits: Array<[string, string]> = [
    ['Équipement', [c.issue.numeroSerie, c.modele].filter(Boolean).join(' · ')],
    ['Projet', c.issue.pn],
    ['Métier', METIERS_LABELS[c.issue.metier] ?? c.issue.metier],
    ['Statut', c.issue.statut],
  ]
  return faits.filter(([, v]) => (v ?? '').trim() !== '')
}

/** Le texte cité en HTML, la mention des personnes prévenues en gras. */
function citationHtml(texte: string, prevenus: readonly PersonneMentionnee[]): string {
  let html = enHtml(texte)
  for (const p of prevenus) {
    const nom = echapper(`@${p.nom.trim()}`)
    html = html.replace(new RegExp(`${echapperRegex(nom)}(?![\\p{L}\\p{N}])`, 'giu'), (m) => `<b style="color:#000000;background-color:#FFF9B0">${m}</b>`)
  }
  return html
}

export function messageMention(c: ContenuMention): MessageMention {
  const ou = OU[c.geste]
  const citation = tronquer(c.texte, LONGUEUR_CITATION)
  const faits = faitsMention(c)
  const avertissement = c.essai ? texteEssaiMention(c.essai) : ''
  const bandeauEssai = avertissement
    ? `<div style="margin-bottom:10px;border-left:3px solid #FFED00;background-color:#F5F5F5;padding:8px 12px;font-size:13px;color:#000000">${echapper(avertissement)}</div>`
    : ''

  const corpsHtml = enveloppeMail({
    intro: `${bandeauEssai}<b style="color:#000000">${echapper(c.auteur)}</b> ${echapper(ou.phrase)}`,
    surtitre: c.issue.id,
    titre: c.issue.titre,
    faits: lignesFaits(faits),
    sections: sectionMail(echapper(ou.section), `<div style="font-size:14px;line-height:20px;color:${TEXTE_MAIL}">${citationHtml(citation, c.prevenus)}</div>`),
    lien: c.lien,
    libelleBouton: 'Ouvrir la fiche dans SCE Issues',
    pied: `Vous recevez ce message parce que ${echapper(c.auteur)} vous a mentionné.<br>Répondre à ce mail écrit directement à ${echapper(c.auteur)}.`,
  })

  const corps: BlocCarte[] = [
    enteteCarte(),
    ...(avertissement ? [texteCarte(avertissement, { size: 'Small', spacing: 'Medium', color: 'Attention' })] : []),
    texteCarte(`${c.auteur} ${ou.phrase}`, { isSubtle: true, size: 'Small', spacing: 'Medium' }),
    texteCarte(`${c.issue.id} · ${c.issue.titre}`, { weight: 'Bolder', size: 'Medium', spacing: 'Small' }),
    { type: 'FactSet', facts: faits.map(([title, value]) => ({ title, value })) },
    { type: 'Container', style: 'emphasis', spacing: 'Medium', items: [texteCarte(ou.section, { weight: 'Bolder', size: 'Small' }), texteCarte(citation, { spacing: 'Small' })] },
  ]

  return {
    objet: tronquer(`${c.essai ? '[Essai] ' : ''}[SCE Issues] ${c.issue.id} · ${c.auteur} vous a mentionné · ${tronquer(c.issue.titre, 80)}`, 250),
    corpsHtml,
    carteTeams: carteAdaptative(corps, c.lien),
  }
}

/** La ligne de SCE_Envois (colonnes du flow 6) : « Répondre à » écrit à l'auteur, pas de copie. */
export function champsMention(issueId: string, m: MessageMention, destinataires: readonly string[], expediteur: string): Record<string, string> {
  return {
    Title: `Mention ${issueId}`,
    Destinataires: destinataires.join(';'),
    Objet: m.objet,
    CorpsHtml: m.corpsHtml,
    CarteTeams: JSON.stringify(m.carteTeams),
    Note: '',
    Expediteur: expediteur,
    CopieExpediteur: 'Non',
  }
}
